/**
 * Generic FIFO (first-in, first-out) queue interface.
 *
 * @typeParam V - The type of values held in the queue.
 */
export interface Queue<V> {
  /**
   * The number of items currently in the queue.
   *
   * @example
   * ```ts
   * const q = new ArrayQueue<number>();
   * q.length; // 0
   * q.enqueue(1);
   * q.length; // 1
   * ```
   */
  length: number

  /**
   * Adds a value to the back of the queue.
   *
   * @param value - The value to enqueue.
   *
   * @example
   * ```ts
   * q.enqueue("hello");
   * q.enqueue("world");
   * q.length; // 2
   * ```
   */
  enqueue(value: V): void

  /**
   * Removes and returns the value at the front of the queue.
   *
   * @returns The value that was at the front, or `undefined` if the queue is empty.
   *
   * @example
   * ```ts
   * q.enqueue(42);
   * q.dequeue(); // 42
   * q.dequeue(); // undefined
   * ```
   */
  dequeue(): V | undefined

  /**
   * Returns the value at the front of the queue **without** removing it.
   *
   * @returns The front value, or `undefined` if the queue is empty.
   *
   * @example
   * ```ts
   * q.enqueue("peek-a-boo");
   * q.peek();  // "peek-a-boo"
   * q.length;  // 1  (unchanged)
   * ```
   */
  peek(): V | undefined

  /**
   * Returns the value at the back of the queue **without** removing it.
   *
   * @returns The back value, or `undefined` if the queue is empty.
   *
   * @example
   * ```ts
   * q.enqueue("first");
   * q.enqueue("last");
   * q.peekLast(); // "last"
   * q.length;     // 2  (unchanged)
   * ```
   */
  peekLast(): V | undefined

  /**
   * Returns `true` when the queue contains no items.
   *
   * @example
   * ```ts
   * q.isEmpty(); // true
   * q.enqueue(false);
   * q.isEmpty(); // false
   * ```
   */
  isEmpty(): boolean

  /**
   * Makes the buffer iterable with for...of loops.
   * Iterates values in logical order (first to last).
   *
   * @yields Values in the queue from index 0 to length-1
   */
  [Symbol.iterator](): IterableIterator<V>
}

/**
 * A single node in the linked-list queue.
 *
 * @typeParam V - The type of value stored in this node.
 */
class Node<V> {
  /** Reference to the next node in the queue, or `null` if this is the last one. */
  next: Node<V> | null = null

  public payload: V

  /**
   * @param payload - The value this node carries.
   */
  constructor(payload: V) {
    this.payload = payload
  }
}

/**
 * A FIFO queue backed by a singly linked list.
 *
 * All core operations run in **O(1)** time. Because nodes are allocated
 * individually, there is no fixed capacity limit.
 *
 * @typeParam V - The type of values held in the queue.
 *
 * @example
 * ```ts
 * const q = new LinkedQueue<number>();
 * q.enqueue(1);
 * q.enqueue(2);
 * q.dequeue(); // 1
 * q.peek();    // 2
 * q.length;    // 1
 * ```
 */
export class LinkedQueue<V> implements Queue<V> {
  private head: Node<V> | null = null
  private tail: Node<V> | null = null

  /** Backing field for the {@link length} getter. */
  private size: number = 0

  get length(): number {
    return this.size
  }

  enqueue(value: V): void {
    const node = new Node(value)

    if (this.head && this.tail) {
      this.tail.next = node
      this.tail = node
    } else {
      this.head = node
      this.tail = node
    }

    this.size++
  }

  dequeue(): V | undefined {
    if (!this.head) return

    const payload = this.head.payload
    this.head = this.head.next

    if (!this.head) {
      // Queue is now empty; drop the dangling tail reference.
      this.tail = null
    }

    this.size--
    return payload
  }

  peek(): V | undefined {
    return this.head ? this.head.payload : undefined
  }

  peekLast(): V | undefined {
    return this.tail ? this.tail.payload : undefined
  }

  isEmpty(): boolean {
    return this.head === null
  }

  *[Symbol.iterator]() {
    let current = this.head
    while (current) {
      yield current.payload
      current = current.next
    }
  }
}

/**
 * A FIFO queue backed by a plain array.
 *
 * Simpler than {@link LinkedQueue} and well-suited for moderate workloads.
 * {@link enqueue} is O(1) amortised; {@link dequeue} is O(n) due to
 * `Array.shift()` — for high-throughput queues consider {@link LinkedQueue}
 * instead.
 *
 * @typeParam V - The type of values held in the queue.
 *
 * @example
 * ```ts
 * const q = new ArrayQueue<number>();
 * q.enqueue(1);
 * q.enqueue(2);
 * q.dequeue(); // 1
 * q.peek();    // 2
 * q.length;    // 1
 * ```
 */
export class ArrayQueue<V> implements Queue<V> {
  private items: V[] = []

  /** Runs in **O(1)** amortised time. */
  enqueue(value: V): void {
    this.items.push(value)
  }

  /** Runs in **O(n)** time due to `Array.shift()`. */
  dequeue(): V | undefined {
    return this.items.shift()
  }

  peek(): V | undefined {
    return this.items[0]
  }

  peekLast(): V | undefined {
    return this.items[this.items.length - 1]
  }

  get length(): number {
    return this.items.length
  }

  isEmpty(): boolean {
    return this.items.length === 0
  }

  *[Symbol.iterator]() {
    for (const item of this.items) {
      yield item
    }
  }
}
